import { IAppContext } from 'App/AppContext';
import { SubscriptionTypes } from 'utils/subscription';
import { appRoles, appRoleSets, appRoleSetTypes } from './appRoles';

//
// What a user may see and open, from the subscription of the organization and the system roles of the user.
// The server enforces the same rules on its endpoints; these only decide what the client shows.
//
export enum FeatureTypes {
  Dashboard = 5,
  //create Skills and write the drafts of one's own Skills (the API's RequireSkillOwner); an admin writes any Skill
  SkillOwner = 10,
  //approve, reject and publish a version (RequireSkillApprover); the API refuses the version's own author,
  //unless they are an administrator (Admin or OrgAdmin)
  SkillApprover = 11,
  Admin = 50,
  AdminAuth = 51,
  AdminSubscription = 52,
  //read the organization's policies (Administration > Policies): administrators and Security Reviewers; only an
  //administrator writes them (Admin)
  PolicyReader = 53,
  //read and export the runtime audit record (Administration > Audit): administrators and Security Reviewers
  AuditReader = 54,
  //read the organization's users with their roles and login (Administration > Users): administrators and Security
  //Reviewers; only an administrator changes them (Admin)
  UserReader = 55,
  TenantAdmin = 300,
  TenantGlobalAdmin = 301,
  GenericManager = 500,
  PreviewFeatures = 1002,
}

// Which subscription types have a feature. Empty array means available for all.
const subscriptionTypeFeatureMapping: Record<FeatureTypes, SubscriptionTypes[]> = {
  [FeatureTypes.Dashboard]: [],
  [FeatureTypes.SkillOwner]: [],
  [FeatureTypes.SkillApprover]: [],
  [FeatureTypes.Admin]: [],
  [FeatureTypes.AdminAuth]: [],
  [FeatureTypes.AdminSubscription]: [],
  [FeatureTypes.PolicyReader]: [],
  [FeatureTypes.AuditReader]: [],
  [FeatureTypes.UserReader]: [],
  [FeatureTypes.TenantAdmin]: [],
  [FeatureTypes.TenantGlobalAdmin]: [],
  [FeatureTypes.GenericManager]: [],
  [FeatureTypes.PreviewFeatures]: [SubscriptionTypes.Internal],
};

// Which roles have a feature. Empty array means available for all.
const roleFeatureMapping: Record<FeatureTypes, appRoles[]> = {
  [FeatureTypes.Dashboard]: appRoleSets[appRoleSetTypes.Users],
  [FeatureTypes.SkillOwner]: appRoleSets[appRoleSetTypes.SkillOwners],
  [FeatureTypes.SkillApprover]: appRoleSets[appRoleSetTypes.SkillApprovers],
  [FeatureTypes.Admin]: appRoleSets[appRoleSetTypes.Admins],
  [FeatureTypes.AdminAuth]: appRoleSets[appRoleSetTypes.Admins],
  [FeatureTypes.AdminSubscription]: appRoleSets[appRoleSetTypes.Admins],
  [FeatureTypes.PolicyReader]: appRoleSets[appRoleSetTypes.PolicyReaders],
  [FeatureTypes.AuditReader]: appRoleSets[appRoleSetTypes.AuditReaders],
  [FeatureTypes.UserReader]: appRoleSets[appRoleSetTypes.UserReaders],
  [FeatureTypes.TenantAdmin]: appRoleSets[appRoleSetTypes.TenantAdmins],
  [FeatureTypes.TenantGlobalAdmin]: [appRoles.TenantAdmin],
  [FeatureTypes.GenericManager]: appRoleSets[appRoleSetTypes.Managers],
  [FeatureTypes.PreviewFeatures]: [],
};

export const hasSubscriptionFeature = (type: SubscriptionTypes, feature: FeatureTypes): boolean => {
  if (type === SubscriptionTypes.Internal) return true;

  const supportedTypes = subscriptionTypeFeatureMapping[feature];

  return supportedTypes.length === 0 || supportedTypes.includes(type);
};

const hasRoleFeature = (roles: number[] | undefined, feature: FeatureTypes): boolean => {
  const requiredRoles = roleFeatureMapping[feature];
  if (requiredRoles.length === 0) return true;

  if (!roles || roles.length === 0) {
    //a user without any role is a plain user
    return requiredRoles.includes(appRoles.User);
  }

  return requiredRoles.some((role) => roles.includes(role));
};

export const hasUserFeature = (appContext: IAppContext, feature: FeatureTypes): boolean => {
  if (!hasSubscriptionFeature(appContext.user.login.subscriptionType, feature)) return false;
  if (!hasRoleFeature(appContext.user.login.userSystemRoles, feature)) return false;

  return true;
};

export const hasUserFeatureGenericManager = (appContext: IAppContext): boolean => {
  return hasRolesFeatureGenericManager(appContext.user.login.userSystemRoles);
};

//The same check from the roles alone, for callers that run before an app context exists: the sign-in
//bootstrap holds the login result but not yet a context to read it from.
export const hasRolesFeatureGenericManager = (roles: number[] | undefined): boolean => {
  return hasRoleFeature(roles, FeatureTypes.GenericManager);
};
